import type { Layout } from "react-grid-layout/legacy";
import type { BackendDashboardSpec, ComponentSpec } from "../Processing/types";
import type { GridWidgetType } from "../types";

export interface DashboardSpecWidget {
  id: string;
  type: GridWidgetType;
  spec?: ComponentSpec;
  meta?: Record<string, unknown>;
}

// Pure mapping from the backend dashboard payload to the grid's widget entries.
// Kept apart from fetchDashboardSpec so the headless renderer can import it
// without the browser API client.

/**
 * Backend `DashboardSpec` → FE widgets/layout transform shared by the load
 * path and Sandbox AI's applyServerSpec. Prefixes each widget id with its
 * type and remaps layout `i` refs to match; backend UUIDs are preserved
 * (never re-IDed), so the serialize round-trip stays stable.
 */
export function mapBackendDashboardSpec(
  info: Partial<Pick<BackendDashboardSpec, "widgets" | "layout">>,
): { widgets: DashboardSpecWidget[]; layout: Layout } {
  const backendWidgets: any[] = info.widgets ?? [];

  const widgets: DashboardSpecWidget[] = backendWidgets.map((w: any) => {
    const prefixedId = `${w.type}-${w.id}`;
    const spec = w.config as ComponentSpec | undefined;
    // A player card has no queries, so the backend stores its controller reference as
    // the declared snake_case field (PlayerCardSpec.single_player_controller_id).
    // Every FE binding reader looks for the camelCase field. Restore it here — the
    // one choke point both load paths share — or the badges, the hover outline, and
    // the next save (sanitizeConfig reads the camel field) all lose the binding.
    if (spec?.type === "player_card") {
      const raw = (spec as any).single_player_controller_id;
      if (raw && !(spec as any).singlePlayerControllerId) {
        (spec as any).singlePlayerControllerId = String(raw);
      }
    }
    return {
      id: prefixedId,
      type: w.type as GridWidgetType,
      spec,
    };
  });

  const idMap = new Map(backendWidgets.map((w: any) => [String(w.id), `${w.type}-${w.id}`]));

  const layout = (info.layout ?? []).map((item: any) => ({
    ...item,
    i: idMap.get(String(item.i)) ?? item.i,
  }));

  return { widgets, layout };
}
